import { Check } from "lucide-react";
import { Link } from "react-router-dom";
import { PublicDocLayout, PublicSectionAnchor } from "@/components/PublicDocLayout";
import { PublicLayout } from "@/components/PublicLayout";
import { PublicPageHero } from "@/components/PublicPageHero";
import {
  ATW_CONTACT_URL,
  ATW_MAILING_ADDRESS,
  ATW_PRIVACY_URL,
  LEGAL_DBA,
  LEGAL_ENTITY,
  PRIVACY_POLICY_UPDATED,
  SUPPORT_EMAIL,
} from "@/lib/publicSite";

const SLACK_ACCESS = [
  {
    feature: "Chat",
    what: "Lists the channels, private channels, DMs, and group DMs you're in. Loads the messages in a conversation you open, and people's names and profile photos. Sends your replies, starts DMs and group DMs, and edits or deletes your own messages.",
    when: "When you open Chat or a conversation, or send, edit, or delete a message",
  },
  {
    feature: "Needs you, on Hub Home",
    what: "Uses Slack's search to find recent messages that mention you and DMs waiting on your reply.",
    when: "When you open Home",
  },
  {
    feature: "Send to Hub",
    what: "Slack sends Hub the message you picked. Hub saves the message's link.",
    when: "When you use the shortcut",
  },
  {
    feature: "Hub AI",
    what: "Searches and reads Slack through Slack's MCP server to answer your question. Sends a message only when you press Send on a draft.",
    when: "When you ask a question",
  },
  {
    feature: "Home tab in Slack",
    what: "Shows getting-started steps and links to support and privacy.",
    when: "When you open the app in Slack",
  },
];

const PROVIDERS = [
  {
    name: "Amazon Web Services",
    role: "Runs the Hub API at api.hub.atw.net, which stores the data Hub keeps, and ATW sign-in at auth.atw.net",
    data: "Everything Hub stores, content passing through the API while Hub handles a request, and your ATW account",
    where: "United States (Hub API: us-east-1, Virginia. ATW sign-in: us-east-2, Ohio)",
  },
  {
    name: "Cloudflare",
    role: "Hosts and delivers the Hub web app at hub.atw.net",
    data: "IP address and request details",
    where: "Global network",
  },
  {
    name: "Anthropic",
    role: "Provides Claude, the AI model behind Hub AI",
    data: "Your Hub AI questions, the conversation so far, and the Hub content Hub AI looks up to answer them: meetings, calendar events, tasks, mail, and Slack messages if you connected Slack to Hub AI",
    where: "United States and other countries where Anthropic runs its models",
  },
  {
    name: "GIPHY",
    role: "GIF search in Chat",
    data: "Your GIF search words, sent through Hub's server. GIF images load from GIPHY, which sees your IP address.",
    where: "United States",
  },
  {
    name: "Google Fonts",
    role: "Fonts for the Hub web app",
    data: "IP address and browser details",
    where: "Global network",
  },
];

const RETENTION = [
  {
    data: "Hub account: your name, email address, and ATW account ID",
    kept: "While your account is open. We delete it within 30 days after you ask us to close it.",
  },
  {
    data: "Sign-in tokens and account details for connected services",
    kept:
      "Until you disconnect the service, uninstall the ATW Hub Slack app, or Zoom sends a deauthorization notice for your Zoom account",
  },
  { data: "Read markers and saved links", kept: "Until you remove them, disconnect Slack, or the app is uninstalled" },
  { data: "Tasks", kept: "Until you delete them, or we close your Hub account" },
  { data: "Slack message text, files, and search results", kept: "Not stored" },
  {
    data: "Hub AI conversations",
    kept:
      "Saved until you delete them in Hub AI's History (Hub keeps your 50 most recent). Conversations where Hub AI read Slack aren't saved; they stay in your browser tab until you reload it.",
  },
  {
    data: "Hub AI suggestions waiting for you to confirm",
    kept: "Up to 2 hours, in the Hub server's memory only",
  },
  {
    data: "What Hub sends Anthropic for Hub AI",
    kept:
      "Anthropic deletes it within 30 days. If Anthropic's safety systems flag a request for breaking its usage policy, Anthropic may keep that request for up to 2 years.",
  },
  { data: "Mail cached in your browser", kept: "Until you clear site data for hub.atw.net in your browser" },
  { data: "Server logs", kept: "Up to 30 days" },
  { data: "Support emails", kept: "As long as needed to resolve your request and keep a record of it" },
];

const PRIVACY_TOC = [
  { id: "collect", label: "What we collect" },
  { id: "slack", label: "Slack" },
  { id: "other-services", label: "Other services" },
  { id: "ai", label: "Hub AI" },
  { id: "providers", label: "Service providers" },
  { id: "retention", label: "How long we keep data" },
  { id: "rights", label: "Your rights" },
  { id: "security", label: "Security" },
  { id: "contact", label: "Contact" },
] as const;

export function PrivacyPage() {
  return (
    <PublicLayout title="ATW Hub Privacy Policy">
      <article className="public-doc">
        <PublicPageHero
          eyebrow="Legal"
          title="ATW Hub Privacy Policy"
          updated={PRIVACY_POLICY_UPDATED}
        />

        <p>
          This policy explains what ATW Hub collects, why, how long we keep it, and how to get a copy or
          have it deleted. It covers the Hub web app at hub.atw.net, the Hub API at api.hub.atw.net, and
          related integrations (including the ATW Hub app for Slack and Zoom). {LEGAL_ENTITY}, doing
          business as {LEGAL_DBA} (“ATW,” “we,” or “us”), operates ATW Hub. ATW's website and other ATW
          services are covered by the <a href={ATW_PRIVACY_URL}>ATW Privacy Policy</a>.
        </p>

        <PublicDocLayout toc={[...PRIVACY_TOC]}>
        <section className="public-summary-card public-doc-section">
          <h2 className="public-section-title">The short version</h2>
          <ul className="public-summary-checklist">
            <li>
              <Check className="public-check-icon" size={18} strokeWidth={2.5} aria-hidden />
              <span>
              Hub shows you your own mail, chat, calendar, and meetings from the services you connect. It
              reads Slack only when you open something in Hub, use Send to Hub, or ask Hub AI.
              </span>
            </li>
            <li>
              <Check className="public-check-icon" size={18} strokeWidth={2.5} aria-hidden />
              <span>We don't store your Slack message text, files, or search results.</span>
            </li>
            <li>
              <Check className="public-check-icon" size={18} strokeWidth={2.5} aria-hidden />
              <span>We never sell your data, use it for ads, or use it to train AI models.</span>
            </li>
            <li>
              <Check className="public-check-icon" size={18} strokeWidth={2.5} aria-hidden />
              <span>
              Disconnect a service and Hub deletes its sign-in tokens. Email{" "}
              <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> to get a copy of anything else or
              have it deleted.
              </span>
            </li>
          </ul>
        </section>

        <section id="collect" className="public-doc-section">
          <h2 className="public-section-title">
            <PublicSectionAnchor sectionId="collect" />
            1. What we collect
          </h2>
          <ul className="public-list">
            <li>
              <strong>Your Hub account:</strong> you sign in to Hub with your ATW account at auth.atw.net.
              Hub gets your name, email address, and ATW account ID from ATW sign-in. The Hub app never asks
              for or stores your password. The ATW Privacy Policy covers your ATW account itself.
            </li>
            <li>
              <strong>Connections:</strong> when you connect Slack, Microsoft Teams chat, Zoom Team Chat
              or Meetings, Gmail, Google Calendar, Zoho Mail or Calendar (if available), or Zoho Cliq,
              that service gives Hub sign-in tokens. We store the tokens (encrypted at rest on the Hub API
              host) and a few details about the account, such as its ID, your name, your email address or
              workspace name, and the permissions you granted, so Hub can show which account is which.
            </li>
            <li>
              <strong>Content from connected services:</strong> Hub loads your mail, messages, events, and
              meetings from each service to show them to you. Sections 2 and 3 explain what Hub keeps.
            </li>
            <li>
              <strong>Things you save in Hub:</strong> settings, tasks, Hub AI conversations (see
              section 4), read markers, and links you save with Send to Hub.
            </li>
            <li>
              <strong>Support messages:</strong> what you send us when you ask for help.
            </li>
            <li>
              <strong>Technical information:</strong> our servers log IP addresses, times, the addresses
              requested, and errors. These logs don't include message text.
            </li>
          </ul>
          <p>
            The Hub web app has no advertising or analytics trackers. It keeps your Hub sign-in and
            settings in your browser so you stay signed in.
          </p>
        </section>

        <section id="slack" className="public-doc-section">
          <h2 className="public-section-title">
            <PublicSectionAnchor sectionId="slack" />
            2. Slack
          </h2>
          <p>
            The ATW Hub app for Slack connects Hub to your Slack account. It acts as you, using a Slack
            sign-in token you approve when you click Allow.
          </p>

          <h3>What Hub accesses and why</h3>
          <div className="public-table-wrap">
            <table className="public-table">
              <thead>
                <tr>
                  <th scope="col">Feature</th>
                  <th scope="col">What Hub reads or does in Slack</th>
                  <th scope="col">When</th>
                </tr>
              </thead>
              <tbody>
                {SLACK_ACCESS.map((row) => (
                  <tr key={row.feature}>
                    <td data-label="Feature">{row.feature}</td>
                    <td data-label="What Hub reads or does in Slack">{row.what}</td>
                    <td data-label="When">{row.when}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h3>What Hub stores</h3>
          <ul className="public-list">
            <li>Your Slack sign-in tokens, encrypted. Only the Hub server can use them.</li>
            <li>
              Your Slack workspace's ID and name, your Slack user ID and name, and the permissions you
              granted.
            </li>
            <li>Read markers: which conversation, and the time of the last message you read in Hub.</li>
            <li>Saved links from Send to Hub: the message's Slack link and when you saved it.</li>
          </ul>

          <h3>What Hub never stores or logs</h3>
          <ul className="public-list">
            <li>
              Message text, files, and search results. Hub holds them in memory only while it handles your
              request, and your browser shows them until you leave the page.
            </li>
            <li>
              Hub AI conversations where Hub AI read Slack. They stay in your browser tab and are cleared
              when you reload it.
            </li>
          </ul>

          <h3>Data Slack sends that Hub doesn't use</h3>
          <p>
            Slack includes extra details in some requests it sends to Hub. For example, when you use Send
            to Hub, Slack sends the message text and channel name, and when you open the app's Home tab,
            Slack sends your user and workspace IDs. Hub uses only what it needs, such as the message link
            and IDs, and discards the rest without storing it.
          </p>

          <h3>Limits Hub follows</h3>
          <ul className="public-list">
            <li>
              Hub sees only what you can see in Slack, and follows your workspace's settings, such as
              message retention and your Slack plan's history limits.
            </li>
            <li>Hub doesn't share your Slack access with anyone else, including people in your organization.</li>
            <li>Hub reads Slack only when you act. It doesn't scan your workspace in the background.</li>
            <li>
              Hub never uses Slack data to train AI models, and never combines or shares it across
              organizations.
            </li>
            <li>
              Slack doesn't let workspace guests use its search features in apps, so guests can't use Needs
              you or Hub AI.
            </li>
          </ul>

          <h3>Disconnecting and uninstalling</h3>
          <ul className="public-list">
            <li>
              <strong>Disconnect:</strong> in Hub, open Settings, then Connections, then Chat, and click
              Disconnect next to the Slack account. Hub revokes its access with Slack and deletes the
              tokens, read markers, and saved links for that account.
            </li>
            <li>
              <strong>Uninstall:</strong> in Slack, remove the ATW Hub app from your workspace (see{" "}
              <Link to="/support">Support</Link> for the steps). When Slack tells Hub the app was
              removed, Hub deletes the tokens, read markers, and saved links for everyone in that
              workspace.
            </li>
            <li>
              Deleting data in Hub doesn't change anything in Slack. Messages you sent from Hub are normal
              Slack messages, and you manage them in Slack.
            </li>
          </ul>
        </section>

        <section id="other-services" className="public-doc-section">
          <h2 className="public-section-title">
            <PublicSectionAnchor sectionId="other-services" />
            3. Other connected services
          </h2>
          <p>
            Hub connects to mail, calendar, chat, and meeting providers you choose. Hub loads content to
            show it to you, sends email or messages or creates events when you ask, and uses this data
            only to run Hub for you. For chat (Microsoft Teams, Zoom Team Chat, Zoho Cliq) Hub loads
            messages live when you open a conversation and does not write message text to disk or logs.
            Short in-memory caches (about 60 seconds for conversation lists) improve performance.
          </p>
          <p>
            <strong>Microsoft Teams:</strong> Hub uses delegated Microsoft Graph permissions for chats you
            can access. Hub stores OAuth tokens and read markers on the API host. Disconnecting in Hub
            deletes them.
          </p>
          <p>
            <strong>Zoom:</strong> Hub uses Zoom OAuth for Team Chat and Meetings. When you remove the app
            from Zoom, Zoom notifies Hub (<code>app_deauthorized</code>) and Hub deletes your Zoom tokens,
            read markers, and related caches. Disconnecting in Hub does the same for the connection you
            remove.
          </p>
          <p>
            Hub's web app keeps a copy of recent mail in your browser (IndexedDB) so Mail opens quickly.
            Signing out of Hub does not clear that mail cache. Clearing your browser's site data for
            hub.atw.net removes it.
          </p>
          <p>
            ATW Hub's use and transfer of information received from Google APIs follows the{" "}
            <a href="https://developers.google.com/terms/api-services-user-data-policy">
              Google API Services User Data Policy
            </a>
            , including its Limited Use requirements: we use Google user data only to provide Hub features
            you request, we do not use it for advertising, and we do not allow humans to read it except
            when you ask for support, for security, or when required by law.
          </p>
        </section>

        <section id="ai" className="public-doc-section">
          <h2 className="public-section-title">
            <PublicSectionAnchor sectionId="ai" />
            4. Hub AI
          </h2>
          <ul className="public-list">
            <li>Hub AI runs on Claude, an AI model made by Anthropic.</li>
            <li>
              When you ask Hub AI something, Hub sends Anthropic your question, the conversation so far,
              and the content Hub AI looks up to answer it: your meetings, calendar events, tasks, and
              mail, and Slack messages if you connected Slack to Hub AI. Hub AI looks things up only when
              you ask it something. It doesn't scan your accounts in the background.
            </li>
            <li>
              Anthropic uses this content only to answer you. Under Anthropic's commercial terms, it
              doesn't use it to train its models, and it deletes it within 30 days. If Anthropic's safety
              systems flag a request for breaking its usage policy, Anthropic may keep that request for
              up to 2 years, or longer if the law requires it.
            </li>
            <li>
              Hub saves your Hub AI conversations so you can return to them in History, and you can delete
              any of them there. Conversations where Hub AI read Slack aren't saved, because Hub doesn't
              store Slack content. They stay in your browser tab until you reload it.
            </li>
            <li>
              The first time a question needs your private channels, DMs, or group DMs, Hub AI asks you
              before it searches them.
            </li>
            <li>
              Hub AI never changes anything on its own. It shows suggested tasks, task changes, and Slack
              drafts as cards, and nothing is saved or sent until you confirm it.
            </li>
            <li>
              AI can be wrong. Check Hub AI's answers and drafts before you rely on them. Hub AI doesn't
              make decisions about you or anyone else.
            </li>
          </ul>
        </section>

        <section className="public-doc-section">
          <h2 className="public-section-title">5. How we use data, and what we don't do</h2>
          <p>We use the data above to:</p>
          <ul className="public-list">
            <li>run Hub for you: show your content, send what you ask it to send, and keep you signed in,</li>
            <li>help you when you contact support,</li>
            <li>keep Hub secure and working, for example by finding errors and preventing abuse,</li>
            <li>tell you about important changes to Hub or this policy.</li>
          </ul>
          <p>
            We don't sell or rent your data, use it for advertising, or use data from Slack or other
            connected services to train AI models. ATW staff don't look at your content unless you ask us
            to help with a specific problem or the law requires it.
          </p>
        </section>

        <section id="providers" className="public-doc-section">
          <h2 className="public-section-title">
            <PublicSectionAnchor sectionId="providers" />
            6. Service providers
          </h2>
          <p>These companies process data for us so Hub can run:</p>
          <div className="public-table-wrap">
            <table className="public-table">
              <thead>
                <tr>
                  <th scope="col">Provider</th>
                  <th scope="col">What it does for Hub</th>
                  <th scope="col">Data it handles</th>
                  <th scope="col">Location</th>
                </tr>
              </thead>
              <tbody>
                {PROVIDERS.map((row) => (
                  <tr key={row.name}>
                    <td data-label="Provider">{row.name}</td>
                    <td data-label="What it does for Hub">{row.role}</td>
                    <td data-label="Data it handles">{row.data}</td>
                    <td data-label="Location">{row.where}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p>
            Slack, Google, Zoho, and Zoom aren't our service providers. You choose to connect them, and
            their own privacy policies cover what they do with your data.
          </p>
          <p>
            We may also share data if the law requires it, to protect someone's safety, or as part of a
            merger or sale of ATW's business. If that happens, we'll tell you before your data falls under
            a different privacy policy.
          </p>
        </section>

        <section className="public-doc-section">
          <h2 className="public-section-title">7. Where data is stored and international transfers</h2>
          <p>
            Hub stores its data in the United States, on Amazon Web Services in the us-east-1 region
            (Virginia). ATW sign-in keeps your ATW account in the us-east-2 region (Ohio). The service
            providers above process data where their table row says.
          </p>
          <p>
            If you use Hub from outside the United States, your data may be processed in the U.S. and
            other countries where our providers operate. We rely on appropriate safeguards where required
            by law (for example standard contractual clauses offered by providers).
          </p>
        </section>

        <section id="retention" className="public-doc-section">
          <h2 className="public-section-title">
            <PublicSectionAnchor sectionId="retention" />
            8. How long we keep data
          </h2>
          <div className="public-table-wrap">
            <table className="public-table">
              <thead>
                <tr>
                  <th scope="col">Data</th>
                  <th scope="col">How long we keep it</th>
                </tr>
              </thead>
              <tbody>
                {RETENTION.map((row) => (
                  <tr key={row.data}>
                    <td data-label="Data">{row.data}</td>
                    <td data-label="How long we keep it">{row.kept}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section id="rights" className="public-doc-section">
          <h2 className="public-section-title">
            <PublicSectionAnchor sectionId="rights" />
            9. Your rights and choices
          </h2>
          <p>You can ask us to:</p>
          <ul className="public-list">
            <li>give you a copy of your data, in a format you can take to another service,</li>
            <li>correct it,</li>
            <li>delete it,</li>
            <li>stop using it for a purpose you object to.</li>
          </ul>
          <p>
            You can also disconnect any service yourself at any time in Settings, then Connections, or
            uninstall the ATW Hub app from Slack.
          </p>
          <p>
            To make a request, email <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> from the
            address on your Hub account, or use the <a href={ATW_CONTACT_URL}>ATW contact form</a>. We may
            need to confirm it's you. We reply within 2 business days and complete requests within 30 days.
            Depending on where you live, for example in the EU, the UK, or California, laws such as the
            GDPR or the CCPA may give you more rights, including the right to complain to your data
            protection authority.
          </p>
        </section>

        <section id="security" className="public-doc-section">
          <h2 className="public-section-title">
            <PublicSectionAnchor sectionId="security" />
            10. Security
          </h2>
          <ul className="public-list">
            <li>All traffic to and from Hub is encrypted with HTTPS (TLS 1.2 or later).</li>
            <li>
              Sign-in tokens for connected services are encrypted, only the Hub server can use them, and
              they're never sent to your browser.
            </li>
            <li>
              Hub checks Slack's signature on every request Slack sends it, and protects each Slack sign-in
              with a one-time check value.
            </li>
            <li>Access to Hub's servers is limited to the ATW staff who run them.</li>
            <li>
              No system is perfectly secure. If we learn of a breach that affects your data, we'll tell you
              without undue delay, and tell the authorities where the law requires it.
            </li>
            <li>
              To report a security problem, email <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>{" "}
              with “Security” in the subject.
            </li>
          </ul>
        </section>

        <section className="public-doc-section">
          <h2 className="public-section-title">11. Children</h2>
          <p>
            Hub is a tool for businesses and isn't meant for children under 16. We don't knowingly collect
            data from children.
          </p>
        </section>

        <section className="public-doc-section">
          <h2 className="public-section-title">12. Changes to this policy</h2>
          <p>
            If we change this policy, we'll update the date at the top. For important changes, we'll tell
            you in Hub or by email before they take effect.
          </p>
        </section>

        <section id="contact" className="public-doc-section">
          <h2 className="public-section-title">
            <PublicSectionAnchor sectionId="contact" />
            13. Contact
          </h2>
          <p>
            {LEGAL_ENTITY}, doing business as {LEGAL_DBA}
            <br />
            {ATW_MAILING_ADDRESS}
            <br />
            Email: <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
            <br />
            Contact form: <a href={ATW_CONTACT_URL}>atw.net/contact</a>
          </p>
        </section>
        </PublicDocLayout>
      </article>
    </PublicLayout>
  );
}
